import React from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  ArrowRight, FileText, Shield, DollarSign, Users, 
  Clock, BarChart3, AlertTriangle, CheckCircle2, 
  Building2, Percent, Calendar, PieChart, Briefcase,
  Target, ChevronRight, Lock, Scale, TrendingUp
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';

const FundTermsPage = () => {
  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 25 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: [0.25, 0.46, 0.45, 0.94]
      }
    }
  };

  // Fund structure data
  const structureItems = [
    { label: 'Entity Type', value: 'Delaware Limited Partnership', icon: Building2 },
    { label: 'Regulation', value: 'Regulation D 506(c)', icon: FileText },
    { label: 'GP Commitment', value: 'Significant Co-Investment', icon: Users },
    { label: 'Investor Type', value: 'Accredited Investors Only', icon: Shield }
  ];

  // Key terms data
  const keyTerms = [
    { label: 'Minimum Investment', value: '$100,000', note: 'May vary by offering' },
    { label: 'Target Hold Period', value: '5-7 Years', note: 'Subject to market conditions' },
    { label: 'Target IRR', value: '20-28%', note: 'Not guaranteed' },
    { label: 'Preferred Return', value: '8%', note: 'Paid before promote' }
  ];

  // Fee structure
  const feeStructure = [
    { type: 'Asset Management', fee: '1-2%', description: 'Annual fee on committed capital' },
    { type: 'Acquisition Fee', fee: '1%', description: 'One-time fee on property acquisitions' },
    { type: 'Carried Interest', fee: '20%', description: 'Above preferred return threshold' }
  ];

  // Risk factors
  const riskFactors = [
    { icon: TrendingUp, title: 'Market Risk', description: 'Real estate values may decline due to economic conditions' },
    { icon: Building2, title: 'Operational Risk', description: 'Property performance may vary from projections' },
    { icon: Lock, title: 'Liquidity Risk', description: 'Investments are illiquid during the hold period' },
    { icon: Scale, title: 'Leverage Risk', description: 'Debt financing may amplify losses' }
  ];

  // Participation steps
  const participationSteps = [
    { step: '1', title: 'Schedule Consultation', description: 'Discuss your investment objectives with our team' },
    { step: '2', title: 'Verify Accreditation', description: 'Complete accredited investor verification process' },
    { step: '3', title: 'Review PPM', description: 'Access Private Placement Memorandum and legal documents' },
    { step: '4', title: 'Execute Subscription', description: 'Complete subscription agreement and fund commitment' }
  ];

  return (
    <>
      <Helmet>
        <title>Real Estate Fund Terms for investors | ASAP Capital</title>
        <meta 
          name="description" 
          content="Review fund structure, fees, minimum investment, and hold periods for ASAP Capital's private equity real estate fund for investors." 
        />
        <meta name="keywords" content="real estate fund terms, private equity fund structure, investor requirements, real estate investment minimum, private placement fund" />
        <link rel="canonical" href="/fund-terms" />
      </Helmet>

      <div className="min-h-screen bg-[#0A1628]">
        <Header />
        
        <main>
          {/* ==================== HERO SECTION ==================== */}
          <section className="relative pt-36 pb-28 lg:pt-44 lg:pb-36 overflow-hidden">
            {/* Background elements */}
            <div className="absolute inset-0 bg-gradient-to-b from-[#0A1628] via-[#0d1a2d] to-[#0A1628]" />
            <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-[#D4AF37]/5 rounded-full blur-[150px] pointer-events-none" />
            <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-[#D4AF37]/3 rounded-full blur-[120px] pointer-events-none" />
            
            <div className="relative z-10 max-w-[1200px] mx-auto px-6 lg:px-12">
              <motion.div
                initial="hidden"
                animate="visible"
                variants={containerVariants}
                className="max-w-3xl mx-auto text-center"
              >
                {/* Pill badge */}
                <motion.div 
                  className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full mb-8 bg-[#D4AF37]/10 border border-[#D4AF37]/20"
                  variants={itemVariants}
                >
                  <FileText className="w-4 h-4 text-[#D4AF37]" />
                  <span className="text-xs font-semibold text-[#D4AF37] uppercase tracking-wider">Fund Terms</span>
                </motion.div>

                {/* H1 */}
                <motion.h1 
                  className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-[1.3] lg:leading-[1.35] mb-6"
                  variants={itemVariants}
                >
                  Fund Terms &{' '}
                  <span className="bg-gradient-to-r from-[#D4AF37] via-[#F5D76E] to-[#D4AF37] bg-clip-text text-transparent">
                    Investment Structure
                  </span>
                </motion.h1>

                {/* Subtitle */}
                <motion.p 
                  className="text-base lg:text-lg text-white/55 leading-relaxed mb-12 max-w-2xl mx-auto"
                  variants={itemVariants}
                >
                  Transparent fund structure, fees, and investment requirements for 
                  investors in our private equity real estate fund.
                </motion.p>

                {/* CTA buttons */}
                <motion.div 
                  className="flex flex-wrap justify-center gap-4"
                  variants={itemVariants}
                >
                  <Link to="/contact">
                    <button className="cta-contact px-6 py-3 h-12 rounded-full text-sm">
                      Request PPM
                      <ArrowRight className="h-4 w-4" />
                    </button>
                  </Link>
                  <Link to="/strategy">
                    <Button variant="outline" className="border-white/20 text-white hover:bg-white/5 px-6 py-3 h-12 rounded-full text-sm">
                      Investment Strategy
                    </Button>
                  </Link>
                </motion.div>
              </motion.div>
            </div>
          </section>

          {/* ==================== FUND OVERVIEW ==================== */}
          <section className="py-20 lg:py-28 relative bg-gradient-to-b from-[#0A1628] to-[#0d1a2d]">
            <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-100px" }}
                variants={containerVariants}
              >
                <div className="grid lg:grid-cols-2 gap-16 items-center">
                  {/* Content */}
                  <div>
                    <motion.span 
                      className="text-[#D4AF37] text-sm font-semibold tracking-wider uppercase mb-4 block"
                      variants={itemVariants}
                    >
                      Overview
                    </motion.span>
                    <motion.h2 
                      className="text-2xl lg:text-3xl font-bold text-white mb-5"
                      variants={itemVariants}
                    >
                      Fund Overview & Objective
                    </motion.h2>
                    <motion.p 
                      className="text-white/55 leading-relaxed mb-6"
                      variants={itemVariants}
                    >
                      ASAP Capital offers investors access to institutional-quality 
                      value-add real estate investments in the Greater Houston market, targeting 
                      multifamily and commercial properties with operational upside.
                    </motion.p>
                    <motion.p 
                      className="text-white/55 leading-relaxed mb-8"
                      variants={itemVariants}
                    >
                      Our fund structure aligns GP and LP interests through significant co-investment, 
                      preferred returns, and transparent fee arrangements.
                    </motion.p>
                    
                    {/* Key points */}
                    <motion.div className="space-y-3" variants={itemVariants}>
                      {['Value-add multifamily and commercial assets', 'Greater Houston and Texas markets', 'Institutional-grade operations', 'Aligned GP/LP structure'].map((point, index) => (
                        <motion.div
                          key={index}
                          className="flex items-center gap-3"
                          initial={{ opacity: 0, x: -20 }}
                          whileInView={{ opacity: 1, x: 0 }}
                          viewport={{ once: true }}
                          transition={{ delay: index * 0.1 }}
                        >
                          <CheckCircle2 className="h-5 w-5 text-[#D4AF37] flex-shrink-0" />
                          <span className="text-white/70">{point}</span>
                        </motion.div>
                      ))}
                    </motion.div>
                  </div>

                  {/* Structure card */}
                  <motion.div variants={itemVariants}>
                    <div className="bg-gradient-to-br from-[#111D35] to-[#0A1628] rounded-2xl p-6 border border-white/10 relative overflow-hidden">
                      <div className="absolute -top-8 -right-8 w-40 h-40 bg-[#D4AF37]/10 rounded-full blur-3xl" />
                      
                      <div className="relative z-10">
                        <h3 className="text-lg font-bold text-white mb-6">Fund Structure</h3>
                        <div className="space-y-4">
                          {structureItems.map((item, index) => (
                            <motion.div
                              key={item.label}
                              className="flex items-center justify-between p-4 bg-[#0A1628]/50 rounded-xl border border-white/10"
                              initial={{ opacity: 0, y: 10 }}
                              whileInView={{ opacity: 1, y: 0 }}
                              viewport={{ once: true }}
                              transition={{ delay: index * 0.1 }}
                            >
                              <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-lg bg-[#D4AF37]/10 flex items-center justify-center">
                                  <item.icon className="h-5 w-5 text-[#D4AF37]" />
                                </div>
                                <span className="text-white/60 text-sm">{item.label}</span>
                              </div>
                              <span className="text-white font-medium text-sm">{item.value}</span>
                            </motion.div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </motion.div>
                </div>
              </motion.div>
            </div>
          </section>

          {/* ==================== KEY TERMS ==================== */}
          <section className="py-20 lg:py-28 relative bg-gradient-to-b from-[#0d1a2d] to-[#0A1628]">
            <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-100px" }}
                variants={containerVariants}
              >
                {/* Header */}
                <div className="text-center mb-16">
                  <motion.span 
                    className="text-[#D4AF37] text-sm font-semibold tracking-wider uppercase mb-4 block"
                    variants={itemVariants}
                  >
                    Investment Terms
                  </motion.span>
                  <motion.h2 
                    className="text-2xl lg:text-3xl font-bold text-white mb-5"
                    variants={itemVariants}
                  >
                    Minimum & Target Returns
                  </motion.h2>
                  <motion.p 
                    className="text-white/50 max-w-2xl mx-auto"
                    variants={itemVariants}
                  >
                    Key investment parameters for investors. All targets are 
                    subject to market conditions and not guaranteed.
                  </motion.p>
                </div>

                {/* Terms grid */}
                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
                  {keyTerms.map((term, index) => (
                    <motion.div
                      key={term.label}
                      className="bg-gradient-to-br from-[#111D35] to-[#0A1628] rounded-2xl p-6 border border-white/10 hover:border-[#D4AF37]/30 transition-all duration-500 text-center group"
                      initial={{ opacity: 0, y: 30 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: index * 0.1 }}
                      whileHover={{ y: -5 }}
                    >
                      <p className="text-white/50 text-sm mb-3">{term.label}</p>
                      <p className="text-3xl font-bold text-[#D4AF37] mb-2 group-hover:scale-105 transition-transform">
                        {term.value}
                      </p>
                      <p className="text-white/30 text-xs">{term.note}</p>
                    </motion.div>
                  ))}
                </div>

                {/* Disclaimer */}
                <motion.div 
                  className="mt-8 text-center"
                  variants={itemVariants}
                >
                  <p className="text-white/30 text-xs max-w-2xl mx-auto">
                    * Target returns represent anticipated performance based on historical data and market analysis. 
                    Actual results may vary. Past performance is not indicative of future results.
                  </p>
                </motion.div>
              </motion.div>
            </div>
          </section>

          {/* ==================== FEE STRUCTURE ==================== */}
          <section className="py-20 lg:py-28 relative bg-gradient-to-b from-[#0A1628] to-[#0d1a2d]">
            <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-100px" }}
                variants={containerVariants}
              >
                <div className="grid lg:grid-cols-2 gap-16 items-start">
                  {/* Content */}
                  <div>
                    <motion.span 
                      className="text-[#D4AF37] text-sm font-semibold tracking-wider uppercase mb-4 block"
                      variants={itemVariants}
                    >
                      Fees
                    </motion.span>
                    <motion.h2 
                      className="text-2xl lg:text-3xl font-bold text-white mb-5"
                      variants={itemVariants}
                    >
                      Fee Structure & Alignment
                    </motion.h2>
                    <motion.p 
                      className="text-white/55 leading-relaxed mb-8"
                      variants={itemVariants}
                    >
                      Our fee structure is designed to align GP and LP interests, with the 
                      majority of GP compensation tied to performance above the preferred return.
                    </motion.p>

                    {/* Fee table */}
                    <motion.div className="space-y-4" variants={itemVariants}>
                      {feeStructure.map((item, index) => (
                        <motion.div
                          key={item.type}
                          className="bg-[#111D35]/50 rounded-xl p-5 border border-white/10 hover:border-[#D4AF37]/20 transition-all"
                          initial={{ opacity: 0, x: -20 }}
                          whileInView={{ opacity: 1, x: 0 }}
                          viewport={{ once: true }}
                          transition={{ delay: index * 0.1 }}
                          whileHover={{ x: 5 }}
                        >
                          <div className="flex items-center justify-between mb-2">
                            <h4 className="text-white font-semibold">{item.type}</h4>
                            <span className="text-[#D4AF37] font-bold text-lg">{item.fee}</span>
                          </div>
                          <p className="text-white/40 text-sm">{item.description}</p>
                        </motion.div>
                      ))}
                    </motion.div>
                  </div>

                  {/* Waterfall visual */}
                  <motion.div variants={itemVariants}>
                    <div className="bg-gradient-to-br from-[#111D35] to-[#0A1628] rounded-2xl p-6 border border-white/10 relative overflow-hidden">
                      <div className="absolute -bottom-8 -left-8 w-40 h-40 bg-[#D4AF37]/10 rounded-full blur-3xl" />
                      
                      <div className="relative z-10">
                        <h3 className="text-lg font-bold text-white mb-6">Distribution Waterfall</h3>
                        
                        {/* Waterfall steps */}
                        <div className="space-y-4">
                          <div className="relative">
                            <div className="flex items-center gap-4">
                              <div className="w-10 h-10 rounded-full bg-emerald-500/20 flex items-center justify-center border border-emerald-500/30 flex-shrink-0">
                                <span className="text-emerald-400 font-bold text-sm">1</span>
                              </div>
                              <div className="flex-1">
                                <p className="text-white font-medium">Return of Capital</p>
                                <p className="text-white/40 text-sm">100% to LPs</p>
                              </div>
                            </div>
                            <div className="absolute left-5 top-10 bottom-0 w-px bg-white/10 h-8" />
                          </div>
                          
                          <div className="relative">
                            <div className="flex items-center gap-4">
                              <div className="w-10 h-10 rounded-full bg-blue-500/20 flex items-center justify-center border border-blue-500/30 flex-shrink-0">
                                <span className="text-blue-400 font-bold text-sm">2</span>
                              </div>
                              <div className="flex-1">
                                <p className="text-white font-medium">Preferred Return (8%)</p>
                                <p className="text-white/40 text-sm">100% to LPs</p>
                              </div>
                            </div>
                            <div className="absolute left-5 top-10 bottom-0 w-px bg-white/10 h-8" />
                          </div>
                          
                          <div className="relative">
                            <div className="flex items-center gap-4">
                              <div className="w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center border border-amber-500/30 flex-shrink-0">
                                <span className="text-amber-400 font-bold text-sm">3</span>
                              </div>
                              <div className="flex-1">
                                <p className="text-white font-medium">GP Catch-Up</p>
                                <p className="text-white/40 text-sm">Until GP receives 20% of profits</p>
                              </div>
                            </div>
                            <div className="absolute left-5 top-10 bottom-0 w-px bg-white/10 h-8" />
                          </div>
                          
                          <div>
                            <div className="flex items-center gap-4">
                              <div className="w-10 h-10 rounded-full bg-[#D4AF37]/20 flex items-center justify-center border border-[#D4AF37]/30 flex-shrink-0">
                                <span className="text-[#D4AF37] font-bold text-sm">4</span>
                              </div>
                              <div className="flex-1">
                                <p className="text-white font-medium">Carried Interest Split</p>
                                <p className="text-white/40 text-sm">80% LP / 20% GP</p>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                </div>
              </motion.div>
            </div>
          </section>

          {/* ==================== CAPITAL & REPORTING ==================== */}
          <section className="py-20 lg:py-28 relative bg-gradient-to-b from-[#0d1a2d] to-[#0A1628]">
            <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-100px" }}
                variants={containerVariants}
              >
                <div className="text-center mb-16">
                  <motion.span 
                    className="text-[#D4AF37] text-sm font-semibold tracking-wider uppercase mb-4 block"
                    variants={itemVariants}
                  >
                    Operations
                  </motion.span>
                  <motion.h2 
                    className="text-2xl lg:text-3xl font-bold text-white mb-5"
                    variants={itemVariants}
                  >
                    Capital & Distributions
                  </motion.h2>
                </div>

                {/* Three columns */}
                <div className="grid md:grid-cols-3 gap-6">
                  <motion.div
                    className="bg-gradient-to-br from-[#111D35] to-[#0A1628] rounded-2xl p-6 border border-white/10"
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.1 }}
                  >
                    <div className="w-12 h-12 rounded-xl bg-[#D4AF37]/10 flex items-center justify-center mb-4 border border-[#D4AF37]/20">
                      <DollarSign className="h-6 w-6 text-[#D4AF37]" />
                    </div>
                    <h3 className="text-lg font-bold text-white mb-3">Capital Calls</h3>
                    <p className="text-white/50 text-sm leading-relaxed">
                      Capital is called as needed for acquisitions and improvements. 
                      Investors receive advance notice for each capital call with 
                      detailed use of proceeds.
                    </p>
                  </motion.div>

                  <motion.div
                    className="bg-gradient-to-br from-[#111D35] to-[#0A1628] rounded-2xl p-6 border border-white/10"
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.2 }}
                  >
                    <div className="w-12 h-12 rounded-xl bg-[#D4AF37]/10 flex items-center justify-center mb-4 border border-[#D4AF37]/20">
                      <Calendar className="h-6 w-6 text-[#D4AF37]" />
                    </div>
                    <h3 className="text-lg font-bold text-white mb-3">Distributions</h3>
                    <p className="text-white/50 text-sm leading-relaxed">
                      Cash flow distributions are made quarterly when available. 
                      Capital gains distributions occur upon property disposition, 
                      following the waterfall structure.
                    </p>
                  </motion.div>

                  <motion.div
                    className="bg-gradient-to-br from-[#111D35] to-[#0A1628] rounded-2xl p-6 border border-white/10"
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.3 }}
                  >
                    <div className="w-12 h-12 rounded-xl bg-[#D4AF37]/10 flex items-center justify-center mb-4 border border-[#D4AF37]/20">
                      <BarChart3 className="h-6 w-6 text-[#D4AF37]" />
                    </div>
                    <h3 className="text-lg font-bold text-white mb-3">Reporting</h3>
                    <p className="text-white/50 text-sm leading-relaxed">
                      Investors receive quarterly financial reports, annual K-1 
                      tax documents, and ongoing updates on property performance 
                      and fund activities.
                    </p>
                  </motion.div>
                </div>
              </motion.div>
            </div>
          </section>

          {/* ==================== RISK FACTORS ==================== */}
          <section className="py-20 lg:py-28 relative bg-gradient-to-b from-[#0A1628] to-[#0d1a2d]">
            <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-100px" }}
                variants={containerVariants}
              >
                <div className="text-center mb-16">
                  <motion.span 
                    className="text-[#D4AF37] text-sm font-semibold tracking-wider uppercase mb-4 block"
                    variants={itemVariants}
                  >
                    Considerations
                  </motion.span>
                  <motion.h2 
                    className="text-2xl lg:text-3xl font-bold text-white mb-5"
                    variants={itemVariants}
                  >
                    Risk Factors
                  </motion.h2>
                  <motion.p 
                    className="text-white/50 max-w-2xl mx-auto"
                    variants={itemVariants}
                  >
                    Real estate investments involve risk. Investors should carefully 
                    review the PPM for complete risk disclosures.
                  </motion.p>
                </div>

                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
                  {riskFactors.map((risk, index) => (
                    <motion.div
                      key={risk.title}
                      className="bg-[#111D35]/50 rounded-2xl p-6 border border-white/10 hover:border-amber-500/20 transition-all group"
                      initial={{ opacity: 0, y: 30 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: index * 0.1 }}
                    >
                      <div className="w-12 h-12 rounded-xl bg-amber-500/10 flex items-center justify-center mb-4 border border-amber-500/20 group-hover:bg-amber-500/20 transition-colors">
                        <risk.icon className="h-6 w-6 text-amber-400" />
                      </div>
                      <h3 className="text-white font-semibold mb-2">{risk.title}</h3>
                      <p className="text-white/40 text-sm">{risk.description}</p>
                    </motion.div>
                  ))}
                </div>

                <motion.div 
                  className="mt-8 text-center"
                  variants={itemVariants}
                >
                  <p className="text-white/30 text-xs max-w-3xl mx-auto">
                    This summary does not constitute a complete description of all risks. 
                    Please review the Private Placement Memorandum for comprehensive risk disclosures 
                    before making any investment decision.
                  </p>
                </motion.div>
              </motion.div>
            </div>
          </section>

          {/* ==================== HOW TO PARTICIPATE ==================== */}
          <section className="py-20 lg:py-28 relative bg-gradient-to-b from-[#0d1a2d] to-[#0A1628]">
            <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-100px" }}
                variants={containerVariants}
              >
                <div className="text-center mb-16">
                  <motion.span 
                    className="text-[#D4AF37] text-sm font-semibold tracking-wider uppercase mb-4 block"
                    variants={itemVariants}
                  >
                    Get Started
                  </motion.span>
                  <motion.h2 
                    className="text-2xl lg:text-3xl font-bold text-white mb-5"
                    variants={itemVariants}
                  >
                    How to Participate
                  </motion.h2>
                </div>

                {/* Steps */}
                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
                  {participationSteps.map((item, index) => (
                    <motion.div
                      key={item.step}
                      className="relative"
                      initial={{ opacity: 0, y: 30 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: index * 0.15 }}
                    >
                      {/* Connector line */}
                      {index < 3 && (
                        <div className="hidden lg:block absolute top-8 left-1/2 w-full h-px bg-gradient-to-r from-[#D4AF37]/30 to-transparent" />
                      )}
                      
                      <div className="bg-gradient-to-br from-[#111D35] to-[#0A1628] rounded-2xl p-6 border border-white/10 hover:border-[#D4AF37]/30 transition-all h-full">
                        <div className="w-14 h-14 rounded-full bg-[#D4AF37]/10 flex items-center justify-center mb-4 border border-[#D4AF37]/20 mx-auto">
                          <span className="text-[#D4AF37] font-bold text-xl">{item.step}</span>
                        </div>
                        <h3 className="text-white font-semibold text-center mb-2">{item.title}</h3>
                        <p className="text-white/40 text-sm text-center">{item.description}</p>
                      </div>
                    </motion.div>
                  ))}
                </div>

                {/* CTA */}
                <motion.div 
                  className="text-center"
                  variants={itemVariants}
                >
                  <Link to="/contact" className="inline-block">
                    <button className="cta-contact px-8 py-3 h-14 rounded-full text-base">
                      Schedule Consultation
                      <ArrowRight className="h-5 w-5" />
                    </button>
                  </Link>
                  <p className="text-white/30 text-sm mt-4">
                    Or email us at{' '}
                    <a href="mailto:info@asapcapital.net" className="text-[#D4AF37] hover:underline">
                      info@asapcapital.net
                    </a>
                  </p>
                </motion.div>
              </motion.div>
            </div>
          </section>

          {/* ==================== ADDITIONAL LINKS ==================== */}
          <section className="py-16 relative bg-[#0A1628]">
            <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={containerVariants}
                className="grid sm:grid-cols-3 gap-4"
              >
                <Link to="/strategy">
                  <motion.div 
                    className="bg-[#111D35]/50 rounded-xl p-5 border border-white/10 hover:border-[#D4AF37]/30 transition-all group flex items-center justify-between"
                    whileHover={{ x: 5 }}
                  >
                    <div className="flex items-center gap-3">
                      <Target className="h-5 w-5 text-[#D4AF37]" />
                      <span className="text-white group-hover:text-[#D4AF37] transition-colors">Investment Strategy</span>
                    </div>
                    <ChevronRight className="h-4 w-4 text-white/30 group-hover:text-[#D4AF37] transition-colors" />
                  </motion.div>
                </Link>
                
                <Link to="/portfolio">
                  <motion.div 
                    className="bg-[#111D35]/50 rounded-xl p-5 border border-white/10 hover:border-[#D4AF37]/30 transition-all group flex items-center justify-between"
                    whileHover={{ x: 5 }}
                  >
                    <div className="flex items-center gap-3">
                      <Building2 className="h-5 w-5 text-[#D4AF37]" />
                      <span className="text-white group-hover:text-[#D4AF37] transition-colors">View Portfolio</span>
                    </div>
                    <ChevronRight className="h-4 w-4 text-white/30 group-hover:text-[#D4AF37] transition-colors" />
                  </motion.div>
                </Link>
                
                <Link to="/team">
                  <motion.div 
                    className="bg-[#111D35]/50 rounded-xl p-5 border border-white/10 hover:border-[#D4AF37]/30 transition-all group flex items-center justify-between"
                    whileHover={{ x: 5 }}
                  >
                    <div className="flex items-center gap-3">
                      <Users className="h-5 w-5 text-[#D4AF37]" />
                      <span className="text-white group-hover:text-[#D4AF37] transition-colors">Meet Our Team</span>
                    </div>
                    <ChevronRight className="h-4 w-4 text-white/30 group-hover:text-[#D4AF37] transition-colors" />
                  </motion.div>
                </Link>
              </motion.div>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
};

export default FundTermsPage;
